import {
  Box,
  Button,
  Center,
  HStack,
  IconButton,
  List,
  ListItem,
  Progress,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { uniqueId } from 'lodash';
import { FileText, Upload, X } from 'lucide-react';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';

export type FileUploadStatus = 'pending' | 'confirmingUpload' | 'complete' | 'failed' | 'aborted';

export type FileUpload = {
  id: string;
  name: string;
  progress: number;
  sizeInBytes: number;
  type: string;
  remove: () => void;
  status: FileUploadStatus;
  failed: boolean;
};

export interface FileUploadContextType {
  files: FileUpload[];
  handleFileChange: (newFiles: File[]) => void | Promise<void>;
  handleRemoveFile: (id: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  multiple?: boolean;
  maxSizePerFile: number;
  hasRequiredFileUploads: boolean;
  hasPendingFileUploads: boolean;
  uploadProgress?: number;
  errors: string[];
}

export const FileUploadContext = createContext<FileUploadContextType | undefined>(undefined);

/**
 * FileUploadRoot component
 *
 * This component serves as the main container for the file upload functionality.
 * It manages the state of uploaded files and provides context for child components.
 *
 * @component
 * @example
 * <FileUploadRoot onChange={handleChange} maxFiles={5} accept={{ 'image/*': ['.jpg', '.png'] }} multiple>
 *   <FileUploadDropzone />
 *   <FileUploadList />
 * </FileUploadRoot>
 *
 * @param {Object} props - The component props
 * @param {ReactNode} props.children - Child components to be rendered within the FileUploadRoot
 * @param {function} [props.onChange] - Callback function triggered when files are added or removed
 * @param {number} [props.maxFiles=50] - Maximum number of files that can be uploaded
 * @param {Record<string, string[]>} [props.accept] - File types to accept, in the format { [mimeType]: [extensions] }
 * @param {boolean} [props.multiple=true] - Whether to allow multiple file selection
 * @param {number} [props.maxSizePerFile=2] - Maximum file size in MB
 */
export function FileUploadRoot({
  children,
  onChange,
  maxFiles = 50, // Default max files to 50
  accept,
  multiple = true,
  maxSizePerFile = 1024, // Default to 1GB
}: {
  children: ReactNode;
  onChange?: (files: File[]) => void;
  maxFiles?: number;
  accept?: Record<string, string[]>;
  multiple?: boolean;
  maxSizePerFile?: number;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleFileChange = (newFiles: File[]) => {
    const invalidFiles = newFiles.filter((file) => file.size > maxSizePerFile * 1024 * 1024);
    const validFiles = newFiles.filter((file) => file.size <= maxSizePerFile * 1024 * 1024);

    if (invalidFiles.length > 0) {
      toast({
        title: 'File size exceeded',
        description: `${invalidFiles.length} file(s) exceed the ${maxSizePerFile}MB limit and were not added.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }

    const updatedFiles = [...files, ...validFiles];

    if (updatedFiles.length > maxFiles) {
      toast({
        title: 'Maximum files exceeded',
        description: `Only the first ${maxFiles} files were added. The rest were ignored.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }

    const finalFiles = updatedFiles.slice(0, maxFiles);
    setFiles(finalFiles);
    if (onChange) {
      onChange(finalFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    if (onChange) {
      onChange(updatedFiles);
    }
  };

  const contextValue = useMemo(
    () =>
      ({
        files: files.map((file, index) => ({
          id: uniqueId('file-'),
          name: file.name,
          type: file.type,
          sizeInBytes: file.size,
          progress: 0,
          remove: () => handleRemoveFile(index),
          status: 'pending' as FileUploadStatus,
          failed: false,
        })),
        handleFileChange,
        handleRemoveFile: (id: string) => {
          const index = files.findIndex((f) => f.name === id);
          if (index > -1) {
            handleRemoveFile(index);
          }
        },
        fileInputRef,
        accept,
        multiple,
        maxFiles,
        maxSizePerFile,
        hasRequiredFileUploads: false,
        hasPendingFileUploads: false,
        uploadProgress: 0,
        errors: [],
      }) satisfies FileUploadContextType,
    [files, handleFileChange, fileInputRef, accept, multiple, maxFiles, maxSizePerFile],
  );

  return (
    <FileUploadContext.Provider value={contextValue}>
      <VStack align="stretch" spacing={2} width="100%" height="100%">
        {children}
      </VStack>
    </FileUploadContext.Provider>
  );
}

/**
 * FileUploadTrigger component
 *
 * This component wraps a child element (typically a button) to trigger the file selection dialog.
 * It must be used within a FileUploadRoot component.
 *
 * @component
 * @example
 * <FileUploadTrigger>
 *   <Button>Select Files</Button>
 * </FileUploadTrigger>
 */
export function FileUploadTrigger({ children }: { children: React.ReactElement }) {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('FileUploadTrigger must be used within a FileUploadContext.Provider');
  }
  const { fileInputRef, accept, handleFileChange, multiple } = context;

  const handleClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  return (
    <>
      <div onClick={handleClick}>{React.cloneElement(children)}</div>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFileChange(Array.from(e.target.files || []));
          e.target.value = ''; // Reset after handling change
        }}
        accept={Object.keys(accept || {}).join(',')}
        multiple={multiple}
      />
    </>
  );
}

/**
 * FileUploadList component
 *
 * This component displays a list of files being uploaded. Each file in the list can be removed individually.
 * It must be used within a FileUploadRoot component.
 *
 * @component
 * @example
 * <FileUploadList />
 *
 * @returns {null|JSX.Element} Returns null if there are no files; otherwise, returns a list of files being uploaded.
 */
export function FileUploadList() {
  const context = useContext(FileUploadContext);
  if (!context) {
    throw new Error('FileUploadList must be used within a FileUploadRoot');
  }
  const { files, hasPendingFileUploads } = context;

  if (files.length === 0) {
    return null;
  }

  return (
    <List spacing={2} w="full">
      {files.map((file) => {
        let progress: number | undefined;
        if (file.status === 'pending') {
          progress = file.progress;
        } else if (file.status === 'confirmingUpload') {
          progress = 100;
        } else if (file.status === 'complete') {
          progress = undefined;
        } else if (file.status === 'failed') {
          progress = undefined;
        }

        return (
          <ListItem key={file.id} display="flex" alignItems="center">
            <HStack
              key={file.id}
              w="full"
              borderRadius="8px"
              borderColor="border.accent"
              borderWidth="1px"
              bgColor="bg.accent.canvas"
              padding="16px"
              gap="16px"
              justifyContent="space-between"
            >
              <HStack spacing="1" alignItems="center" minW={0}>
                <Box flexShrink={0} width="30px">
                  <FileText size="30px" />
                </Box>
                <Text isTruncated maxW="100%" flexShrink={1}>
                  {file.name}
                </Text>
              </HStack>

              <HStack alignItems="center" justifyContent="flex-end">
                {file.failed ? (
                  <Text color="red.500">Failed</Text>
                ) : typeof progress !== 'undefined' ? (
                  <Box
                    width={{
                      base: '100px',
                      sm: '150px',
                      md: '200px',
                      lg: '250px',
                    }}
                  >
                    <Progress
                      value={progress}
                      isAnimated={true}
                      hasStripe={true}
                      sx={{
                        '& > div': {
                          transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        },
                      }}
                    />
                  </Box>
                ) : null}

                <IconButton
                  aria-label="Remove"
                  icon={<X size="20px" />}
                  variant="ghost"
                  color="fg.default"
                  onClick={file.remove}
                  disabled={hasPendingFileUploads}
                />
              </HStack>
            </HStack>
          </ListItem>
        );
      })}
    </List>
  );
}

/**
 * FileUploadDropzone component
 *
 * This component creates a dropzone area for drag-and-drop file uploads.
 * It also allows for custom content to be displayed within the dropzone.
 * It must be used within a FileUploadRoot component.
 *
 * @component
 * @example
 * <FileUploadDropzone>
 *   <Text>Drag files here or click to select</Text>
 * </FileUploadDropzone>
 */
export function FileUploadDropzone({
  children,
  fileTypeLabels,
}: {
  children?: ReactNode;
  fileTypeLabels?: string[];
}) {
  const context = useContext(FileUploadContext);
  const toast = useToast();

  if (!context) {
    throw new Error('FileUploadDropzone must be used within a FileUploadRoot');
  }
  const { handleFileChange, accept, multiple, maxFiles, maxSizePerFile } = context;

  const handleDropRejected = useCallback(
    (fileRejections: FileRejection[], _event: DropEvent) => {
      let errorMessage = '';
      fileRejections.forEach((rejection) => {
        rejection.errors.forEach((error) => {
          switch (error.code) {
            case 'too-many-files':
              errorMessage = `Too many files. Max allowed is` + ' ' + maxFiles + '.';
              break;
            case 'file-too-large':
              errorMessage = `File is too large. Max size is` + ' ' + maxSizePerFile + 'MB.';
              break;
            // case 'file-too-small':
            //   `File is too small. Min size is {minSize / 1024}KB.`;
            //   break;
            case 'file-invalid-type':
              errorMessage = fileTypeLabels?.length
                ? `Invalid file type. Accepted types are:` + ' ' + fileTypeLabels.join(', ')
                : `Invalid file type.`;
              break;
            default:
              errorMessage = `An error occurred while processing the file.`;
          }
        });
      });
      toast({
        title: `File Upload Error`,
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    [toast, fileTypeLabels, maxFiles, maxSizePerFile],
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        await handleFileChange(acceptedFiles);
      } catch {
        toast({
          title: `File Upload Error`,
          description: `We could not upload your file. Please try again.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [handleFileChange, toast],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => void handleDrop(acceptedFiles),
    onDropRejected: handleDropRejected,
    accept,
    multiple,
    maxFiles,
    maxSize: maxSizePerFile * 1024 * 1024, // TODO: fix this
  });

  return (
    <Center
      {...getRootProps()}
      borderWidth="2px"
      borderRadius="lg"
      borderStyle="dotted"
      px="6"
      py="4"
      height="100%"
      width="100%"
      minHeight="200px"
      borderColor={isDragActive ? 'border.accent' : 'brand.300'}
      transition="all 0.2s"
      _hover={{ borderColor: 'border.accent' }}
    >
      <input {...getInputProps()} />
      <VStack spacing="3">
        {isDragActive ? (
          <Text textStyle="sm" color="fg.muted">
            Drop the files here...
          </Text>
        ) : (
          <Box width="100%" height="100%">
            {children ? (
              children
            ) : (
              <VStack spacing="3">
                <IconButton
                  aria-label={'Upload'}
                  icon={<Upload size="24" color="fg.default" />}
                  variant="ghost"
                />
                <VStack spacing="1">
                  <HStack spacing="1" whiteSpace="nowrap">
                    <Button variant="text" color="fg.muted" size="md">
                      Click to upload
                    </Button>
                    <Text textStyle="sm" color="fg.muted">
                      or drag and drop
                    </Text>
                    <Text textStyle="sm" color="fg.muted">
                      (up to {maxFiles} files, {maxSizePerFile}MB each)
                    </Text>
                  </HStack>
                </VStack>
              </VStack>
            )}
          </Box>
        )}
      </VStack>
    </Center>
  );
}
